import Auth from "../Auth";
import { useProjectStore } from "../stores/projectStore";

type EditorSelectionHint = {
  documentId: string;
  pmFrom: number;
  pmTo: number;
  plainTextStart: number;
  plainTextEnd: number;
};

type SyncPayload = {
  projectId: string;
  documentId: string;
  passage?: string;
  passageHtml?: string;
  editorSelectionHint?: EditorSelectionHint;
};

type SyncedState =
  | { kind: "cleared" }
  | { kind: "payload"; fingerprint: string };

function isCompleteEditorSelectionHint(
  hint: EditorSelectionHint | null | undefined,
): hint is EditorSelectionHint {
  if (!hint?.documentId?.trim()) return false;
  const { pmFrom, pmTo, plainTextStart, plainTextEnd } = hint;
  return (
    Number.isFinite(pmFrom)
    && Number.isFinite(pmTo)
    && pmTo > pmFrom
    && Number.isFinite(plainTextStart)
    && Number.isFinite(plainTextEnd)
    && plainTextEnd > plainTextStart
  );
}

const pendingPutTimers = new Map<string, ReturnType<typeof setTimeout>>();
const pendingClearTimers = new Map<string, ReturnType<typeof setTimeout>>();
const lastSyncedByKey = new Map<string, SyncedState>();
const desiredPutByKey = new Map<string, { payload: SyncPayload; fingerprint: string }>();
const desiredClearByKey = new Map<string, Pick<SyncPayload, "projectId" | "documentId">>();
const RIGHT_BAR_MODE_STORAGE_PREFIX = "@sudowrite:rightbar-mode:";
const DEBOUNCE_MS = 1000;

function isRightBarChatTabActive(projectId: string): boolean {
  try {
    const savedMode = localStorage.getItem(`${RIGHT_BAR_MODE_STORAGE_PREFIX}${projectId}`);
    return savedMode !== "history";
  } catch {
    return true;
  }
}

export function isEditorSelectionContextSyncEnabled(): boolean {
  const state = useProjectStore.getState();
  if (state.columnWidths?.right?.collapsed === true) return false;

  const projectId = typeof state.project?.id === "string" ? state.project.id.trim() : "";
  if (!projectId) return false;

  return isRightBarChatTabActive(projectId);
}

function syncKey(projectId: string, documentId: string): string {
  return `${projectId}:${documentId}`;
}

function payloadFingerprint(payload: SyncPayload): string {
  return JSON.stringify({
    projectId: payload.projectId,
    documentId: payload.documentId,
    passage: payload.passage ?? "",
    passageHtml: payload.passageHtml ?? "",
    editorSelectionHint: payload.editorSelectionHint ?? null,
  });
}

function cancelPendingPut(key: string): void {
  const existingPut = pendingPutTimers.get(key);
  if (existingPut) {
    clearTimeout(existingPut);
    pendingPutTimers.delete(key);
  }
}

function cancelPendingClear(key: string): void {
  const existingClear = pendingClearTimers.get(key);
  if (existingClear) {
    clearTimeout(existingClear);
    pendingClearTimers.delete(key);
  }
}

function resolveProjectAndDocumentIds(): { projectId: string; documentId: string } | null {
  const state = useProjectStore.getState();
  const projectId = typeof state.project?.id === "string" ? state.project.id.trim() : "";
  const documentId = typeof state.document?.id === "string" ? state.document.id.trim() : "";
  if (!projectId || !documentId) return null;
  return { projectId, documentId };
}

async function putSelectionContext(payload: SyncPayload): Promise<void> {
  await Auth.fetchWithIdToken("/api/editor/selection-context", {
    method: "PUT",
    ...payload,
  });
}

async function deleteSelectionContext(payload: Pick<SyncPayload, "projectId" | "documentId">): Promise<void> {
  const query = new URLSearchParams({
    projectId: payload.projectId,
    documentId: payload.documentId,
  });
  await Auth.fetchWithIdToken(`/api/editor/selection-context?${query.toString()}`, {
    method: "DELETE",
  });
}

function schedulePutRetry(key: string, payload: SyncPayload, fingerprint: string): void {
  if (desiredPutByKey.get(key)?.fingerprint !== fingerprint) return;
  if (!isEditorSelectionContextSyncEnabled()) return;
  cancelPendingPut(key);
  pendingPutTimers.set(
    key,
    setTimeout(() => {
      pendingPutTimers.delete(key);
      executePutSync(key, payload, fingerprint);
    }, DEBOUNCE_MS),
  );
}

function scheduleClearRetry(key: string, ids: Pick<SyncPayload, "projectId" | "documentId">): void {
  const desired = desiredClearByKey.get(key);
  if (!desired || desired.projectId !== ids.projectId || desired.documentId !== ids.documentId) return;
  if (!isEditorSelectionContextSyncEnabled()) return;
  cancelPendingClear(key);
  pendingClearTimers.set(
    key,
    setTimeout(() => {
      pendingClearTimers.delete(key);
      executeClearSync(key, ids);
    }, DEBOUNCE_MS),
  );
}

function executePutSync(key: string, payload: SyncPayload, fingerprint: string): void {
  if (!isEditorSelectionContextSyncEnabled()) return;
  void putSelectionContext(payload)
    .then(() => {
      lastSyncedByKey.set(key, { kind: "payload", fingerprint });
    })
    .catch(() => {
      lastSyncedByKey.delete(key);
      schedulePutRetry(key, payload, fingerprint);
    });
}

function executeClearSync(key: string, ids: Pick<SyncPayload, "projectId" | "documentId">): void {
  if (!isEditorSelectionContextSyncEnabled()) return;
  void deleteSelectionContext(ids)
    .then(() => {
      lastSyncedByKey.set(key, { kind: "cleared" });
    })
    .catch(() => {
      lastSyncedByKey.delete(key);
      scheduleClearRetry(key, ids);
    });
}

function enqueuePutSync(key: string, payload: SyncPayload, fingerprint: string): void {
  const lastSynced = lastSyncedByKey.get(key);
  if (lastSynced?.kind === "payload" && lastSynced.fingerprint === fingerprint) {
    cancelPendingClear(key);
    cancelPendingPut(key);
    return;
  }

  cancelPendingClear(key);
  cancelPendingPut(key);

  pendingPutTimers.set(
    key,
    setTimeout(() => {
      pendingPutTimers.delete(key);
      executePutSync(key, payload, fingerprint);
    }, DEBOUNCE_MS),
  );
}

function enqueueClearSync(key: string, ids: Pick<SyncPayload, "projectId" | "documentId">): void {
  if (lastSyncedByKey.get(key)?.kind === "cleared") {
    cancelPendingPut(key);
    return;
  }

  cancelPendingPut(key);
  cancelPendingClear(key);

  pendingClearTimers.set(
    key,
    setTimeout(() => {
      pendingClearTimers.delete(key);
      executeClearSync(key, ids);
    }, DEBOUNCE_MS),
  );
}

/** Flush any desired selection-context state after chat sync becomes available again. */
export function reconcileEditorSelectionContextSync(): void {
  if (!isEditorSelectionContextSyncEnabled()) return;

  const ids = resolveProjectAndDocumentIds();
  if (!ids) return;

  const key = syncKey(ids.projectId, ids.documentId);
  const desiredPut = desiredPutByKey.get(key);
  if (desiredPut) {
    enqueuePutSync(key, desiredPut.payload, desiredPut.fingerprint);
    return;
  }

  if (desiredClearByKey.has(key)) {
    enqueueClearSync(key, ids);
  }
}

export function scheduleSyncEditorSelectionContext(params: {
  passage: string;
  passageHtml?: string;
  editorSelectionHint?: EditorSelectionHint | null;
}): void {
  const ids = resolveProjectAndDocumentIds();
  const passage = String(params.passage ?? "").trim();
  const hint = isCompleteEditorSelectionHint(params.editorSelectionHint)
    ? params.editorSelectionHint
    : undefined;
  if (!ids || (!passage && !hint)) return;

  const payload: SyncPayload = {
    projectId: ids.projectId,
    documentId: ids.documentId,
    ...(passage ? { passage } : {}),
    ...(params.passageHtml?.trim() ? { passageHtml: params.passageHtml.trim() } : {}),
    ...(hint ? { editorSelectionHint: hint } : {}),
  };

  const key = syncKey(ids.projectId, ids.documentId);
  const fingerprint = payloadFingerprint(payload);

  desiredPutByKey.set(key, { payload, fingerprint });
  desiredClearByKey.delete(key);

  if (!isEditorSelectionContextSyncEnabled()) return;

  enqueuePutSync(key, payload, fingerprint);
}

export function scheduleClearEditorSelectionContext(): void {
  const ids = resolveProjectAndDocumentIds();
  if (!ids) return;

  const key = syncKey(ids.projectId, ids.documentId);

  desiredClearByKey.set(key, ids);
  desiredPutByKey.delete(key);

  if (!isEditorSelectionContextSyncEnabled()) return;

  enqueueClearSync(key, ids);
}

/** Immediately clear cached selection context (e.g. when the highlight chip is dismissed). */
export function flushClearEditorSelectionContext(): void {
  const ids = resolveProjectAndDocumentIds();
  if (!ids) return;

  const key = syncKey(ids.projectId, ids.documentId);

  desiredClearByKey.set(key, ids);
  desiredPutByKey.delete(key);
  cancelPendingPut(key);
  cancelPendingClear(key);

  if (!isEditorSelectionContextSyncEnabled()) return;

  executeClearSync(key, ids);
}

/** @internal Test helper to reset module-level sync state between tests. */
export function resetEditorSelectionContextSyncStateForTests(): void {
  for (const timer of pendingPutTimers.values()) clearTimeout(timer);
  for (const timer of pendingClearTimers.values()) clearTimeout(timer);
  pendingPutTimers.clear();
  pendingClearTimers.clear();
  lastSyncedByKey.clear();
  desiredPutByKey.clear();
  desiredClearByKey.clear();
}
